import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PermissionType } from 'src/app/core/models/permissions.enum';
import { UserService } from 'src/app/core/services/auth/user.service';
import { MembershipService } from 'src/app/core/services/membership/membership.service';
import { environment } from 'src/environments/environment';

function getWindow(): any {
  return window;
}

// Support chat and the knowledge center are HubSpot's. With hubspotEnabled off (local development)
// neither is set up or opened, and no HubSpot token is requested.
@Injectable({
  providedIn: 'root',
})
export class KnowledgeCenterService {
  hubspotChatContainer: HTMLElement | null;

  constructor(private httpClient: HttpClient, private userService: UserService, private membershipService: MembershipService) {
    membershipService.checkPermissionAsync(PermissionType.OpenKnowledgeCenterWebWidget).subscribe(allowed => {
      if (allowed && environment.hubspotEnabled) {
        this.configureWidget();
        this.authenticateWidget();
        this.closeWebWidget();
      }
    });
    this.hubspotChatContainer = document.querySelector('#hubspot-chat-container') as HTMLElement;
  }

  isWebWidgetOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isChatIconVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  window = getWindow();

  public getChatIconVisibleObservable$(): Observable<boolean> {
    return this.isChatIconVisible.asObservable();
  }

  public toggleKnowledgeCenterWidget(): void {
    const isOpen = this.isWebWidgetOpen.getValue();
    isOpen ? this.closeWebWidget() : this.openWebWidget();
    this.isChatIconVisible.next(isOpen);
  }

  private configureWidget(): void {
    this.window.addEventListener('hubspot-chat-close-btn-clicked', () => {
      this.closeWebWidget();
    });
  }

  public authenticateWidget(): void {
    if (environment.hubspotEnabled) {
      this.authenticateHubspotWidget();
    }
  }

  public authenticateHubspotWidget(): void {
    this.userService.getCurrentUser().subscribe(user => {
      this.getHubspotIdentificationToken().subscribe(token => {
        this.window.hsConversationsSettings = {
          ...this.window.hsConversationsSettings,
          identificationEmail: user.email,
          identificationToken: token,
        };
      });
      this.showChatIcon();
    });
  }

  public logout(): void {
    this.closeWebWidget();
    this.logoutWebWidget();
    this.hideChatIcon();
  }

  public showChatIcon() {
    this.isChatIconVisible.next(true);
  }

  public hideChatIcon() {
    this.isChatIconVisible.next(false);
  }

  public openWebWidget(): void {
    if (!environment.hubspotEnabled) {
      return;
    }
    this.openHubspotWebWidget();
    this.isWebWidgetOpen.next(true);
  }

  public closeWebWidget(): void {
    this.closeHubspotWebWidget();
    this.isWebWidgetOpen.next(false);
  }

  private logoutWebWidget(): void {
    this.logoutHubspotWebWidget();
  }

  private openHubspotWebWidget(): void {
    this.window.HubSpotConversations.widget.load({ widgetOpen: true });
    if (this.hubspotChatContainer) {
      //@ts-ignore
      this.hubspotChatContainer.style.display = 'block';
    }
  }

  private closeHubspotWebWidget(): void {
    if (this.hubspotChatContainer) {
      //@ts-ignore
      this.hubspotChatContainer.style.display = 'none';
    }
  }

  private logoutHubspotWebWidget(): void {
    this.window.hsConversationsSettings = {
      ...this.window.hsConversationsSettings,
      identificationEmail: '',
      identificationToken: '',
    };
  }

  public openKnowledgeCenter(redirectUrl: string = ''): void {
    if (!environment.hubspotEnabled) {
      return;
    }
    this.getSSOToken().subscribe(jwtToken => {
      var url = '';
      //open knowledge center from Xosphere
      if (redirectUrl == '') {
        url = `${environment.knowledgeCenterUrl}/_hcms/mem/jwt?jwt=${jwtToken}&redirect_url=${environment.knowledgeCenterUrl}`;
        window.open(url, '_blank', 'noopener,noreferrer');

        //Hubspot SSO verification
      } else if (redirectUrl.includes('_hcms/mem/jwt/verify')) {
        url = `${redirectUrl}?jwt=${jwtToken}`;
        window.location.href = url;

        //Hubspot SSO login
      } else {
        url = `${environment.knowledgeCenterUrl}/_hcms/mem/jwt?jwt=${jwtToken}&redirect_url=${redirectUrl}`;
        window.location.href = url;
      }
    });
  }

  private getHubspotIdentificationToken(): Observable<string> {
    return this.httpClient.get(`${environment.apiUrl}/Support/GetHubspotIdentificationToken`, { responseType: 'text' });
  }

  public getSSOToken(): Observable<string> {
    return this.httpClient.get(`${environment.apiUrl}/Support/GetSSOToken`, { responseType: 'text' });
  }
}
